import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from "react";
import { IconSearch } from "../icons";
import { DrawerButton } from "./AppShell";
import { Dialog } from "./Dialog";

export interface TopBarProps {
  /** Left side, usually a GlobalSearch. */
  search?: ReactNode;
  /** Right side: icon buttons (notifications, sign-out...). */
  actions?: ReactNode;
}

/** The 64 px top bar: the drawer's menu button (under 760 px), the search pill and the actions on the right. */
export function TopBar({ search, actions }: TopBarProps) {
  return (
    <header className="lcc-topbar">
      <DrawerButton />
      {search}
      <span className="lcc-topbar__spacer" />
      {actions}
    </header>
  );
}

export interface CommandItem {
  id: string;
  label: string;
  /** Second line (module, what it opens). */
  description?: string;
  /** Group heading in the palette ("Go to", "Tickets"...). */
  group?: string;
  href: string;
  /** Extra words that match this item. */
  keywords?: string[];
}

export interface GlobalSearchProps {
  placeholder?: string;
  /** Static items (navigation), filtered by label, description and keywords. */
  items: CommandItem[];
  /** Optional id lookups (ticket id, bill id...): called with the query; answers more items. */
  lookup?: (query: string) => Promise<CommandItem[]>;
  onSelect: (item: CommandItem) => void;
  /** One line saying what can be searched (shown in the palette). */
  hint?: string;
}

function matches(item: CommandItem, q: string): boolean {
  const hay = [item.label, item.description ?? "", ...(item.keywords ?? [])].join(" ").toLowerCase();
  return q
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .every((w) => hay.includes(w));
}

/**
 * The search pill of the top bar. Clicking it or pressing Ctrl K (Cmd K) opens a command palette over the navigation,
 * plus whatever `lookup` answers (e.g. jump to a ticket by its id). Arrow keys move, Enter opens, Escape closes.
 */
export function GlobalSearch({ placeholder = "Search…", items, lookup, onSelect, hint }: GlobalSearchProps) {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.ctrlKey || e.metaKey) && !e.altKey && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen(true);
      }
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);
  return (
    <>
      <button type="button" className="lcc-search" aria-haspopup="dialog" aria-keyshortcuts="Control+K" onClick={() => setOpen(true)}>
        <IconSearch size={18} />
        <span className="lcc-search__placeholder">{placeholder}</span>
        <span className="lcc-kbd" aria-hidden="true">
          Ctrl K
        </span>
      </button>
      {open && (
        <CommandPalette
          items={items}
          lookup={lookup}
          hint={hint}
          placeholder={placeholder}
          onClose={() => setOpen(false)}
          onSelect={(item) => {
            setOpen(false);
            onSelect(item);
          }}
        />
      )}
    </>
  );
}

interface PaletteProps {
  items: CommandItem[];
  lookup?: (query: string) => Promise<CommandItem[]>;
  hint?: string;
  placeholder: string;
  onClose: () => void;
  onSelect: (item: CommandItem) => void;
}

export function CommandPalette({ items, lookup, hint, placeholder, onClose, onSelect }: PaletteProps) {
  const [query, setQuery] = useState("");
  const [found, setFound] = useState<CommandItem[]>([]);
  const [searching, setSearching] = useState(false);
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listId = useId();

  useEffect(() => {
    const q = query.trim();
    if (!lookup || q.length < 3) {
      setFound([]);
      setSearching(false);
      return;
    }
    let live = true;
    setSearching(true);
    const t = setTimeout(() => {
      lookup(q)
        .then((r) => live && setFound(r))
        .catch(() => live && setFound([]))
        .finally(() => live && setSearching(false));
    }, 250);
    return () => {
      live = false;
      clearTimeout(t);
    };
  }, [query, lookup]);

  const results = useMemo(() => {
    const q = query.trim();
    const nav = q ? items.filter((i) => matches(i, q)) : items;
    return [...found, ...nav].slice(0, 50);
  }, [items, found, query]);

  useEffect(() => setActive(0), [query, found]);

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const r = results[active];
      if (r) onSelect(r);
    }
  }

  let lastGroup: string | undefined;
  const optionId = (i: number) => `${listId}-o${i}`;
  return (
    <Dialog open title="Search" hideTitle onClose={onClose} className="lcc-palette" initialFocusRef={inputRef as React.RefObject<HTMLElement>}>
      <div className="lcc-palette__field">
        <IconSearch size={18} />
        <input
          ref={inputRef}
          className="lcc-palette__input"
          type="search"
          role="combobox"
          aria-label="Search"
          aria-expanded={results.length > 0}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={results[active] ? optionId(active) : undefined}
          placeholder={placeholder}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onKeyDown}
        />
      </div>
      {hint && <p className="lcc-palette__hint">{hint}</p>}
      <ul id={listId} role="listbox" aria-label="Results" className="lcc-palette__list">
        {results.map((r, i) => {
          const heading = r.group && r.group !== lastGroup ? r.group : null;
          lastGroup = r.group;
          return (
            <li key={`${r.group ?? ""}:${r.id}`} role="presentation">
              {heading && (
                <div className="lcc-palette__group" role="presentation">
                  {heading}
                </div>
              )}
              <div
                id={optionId(i)}
                role="option"
                aria-selected={i === active}
                className="lcc-palette__item"
                onMouseEnter={() => setActive(i)}
                onClick={() => onSelect(r)}
              >
                <span className="lcc-palette__label">{r.label}</span>
                {r.description && <span className="lcc-palette__desc">{r.description}</span>}
              </div>
            </li>
          );
        })}
      </ul>
      <p className="lcc-palette__hint" role="status">
        {searching ? "Looking it up…" : results.length === 0 ? "Nothing matches." : ""}
      </p>
    </Dialog>
  );
}
